/**
 * Information-security risk register (ISO 27001 clause 6.1 / NIS2 Art 21 /
 * DORA ICT risk management).
 *
 * The register table plus a 5×5 likelihood × impact heatmap, and a create/edit
 * sheet that captures the inherent assessment, the treatment decision, the
 * residual assessment, an owner, a review date and links to the gaps, controls
 * and frameworks the risk bears on.
 *
 * COLOUR
 * Risk bands are a verdict, so they use the reserved status ramp
 * (good → critical) via TONE_FOR_STATUS, never the categorical series colours.
 * The heatmap colours every cell by its band — the matrix is a fixed ramp — and
 * overlays the live count; empty cells are dimmed, not hidden, so the shape of
 * the matrix stays legible.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton, StatTile,
  ErrorNotice, TONE_FOR_STATUS, type Tone,
} from '../components/ui';
import { IconShield } from '../components/icons';
import {
  RISK_CATEGORIES, RISK_TREATMENTS, RISK_REGISTER_STATUSES,
  riskScore, riskBand,
} from '@opencomply/shared';

// ---------------------------------------------------------------------------
//  Types mirroring the API projection
// ---------------------------------------------------------------------------

interface RiskLink { id: string; entityType: string; entityId: string; label?: string | null }
interface Risk {
  id: string;
  reference: string;
  title: string;
  description: string | null;
  category: string;
  likelihood: number;
  impact: number;
  inherentScore: number;
  treatment: string | null;
  treatmentPlan: string | null;
  residualLikelihood: number | null;
  residualImpact: number | null;
  residualScore: number | null;
  status: string;
  reviewDate: string | null;
  owner: { id: string; firstName: string; lastName: string } | null;
  links: RiskLink[];
  _count?: { links: number };
}
interface MatrixCell { likelihood: number; impact: number; count: number }
interface Matrix {
  total: number;
  cells: MatrixCell[];
  byCategory: Record<string, number>;
  byStatus: Record<string, number>;
  byTreatment: Record<string, number>;
}

const AXES = [1, 2, 3, 4, 5] as const;
/** The 1–5 axis label key and English fallback, as functions so indexing a
 *  partial record never yields `undefined` under noUncheckedIndexedAccess. */
const axisKey = (n: number): string => `risk.axis.${n}`;
const axisDefault = (n: number): string =>
  (['', 'Very low', 'Low', 'Medium', 'High', 'Very high'][n] ?? '');

const BAND_VAR: Record<string, string> = {
  LOW: '--status-good', MEDIUM: '--status-warning', HIGH: '--status-serious', CRITICAL: '--status-critical',
};

function scoreTone(score: number): Tone {
  return (TONE_FOR_STATUS[riskBand(score)] ?? 'neutral') as Tone;
}

// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------

export function RiskRegisterPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<Risk | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = allows('risk.manage');

  const { data, isLoading } = useQuery({
    queryKey: ['risk-register', { search, status, category }],
    queryFn: () =>
      api.get<{ items: Risk[]; total: number }>(
        `/risk-register${qs({ search, status, category, pageSize: 500 })}`,
      ),
  });

  const { data: matrix } = useQuery({
    queryKey: ['risk-register', 'matrix'],
    queryFn: () => api.get<Matrix>('/risk-register/matrix'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/risk-register/${id}`),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['risk-register'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const items = data?.items ?? [];
  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—';

  const cellMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of matrix?.cells ?? []) m.set(`${c.likelihood}:${c.impact}`, c.count);
    return m;
  }, [matrix]);

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="oc-h1">{t('risk.title', 'Risk register')}</h1>
          <p className="oc-muted mt-1">
            {t('risk.subtitle', 'Identify, assess and treat information-security risks.')}
          </p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => { setError(null); setCreating(true); }}>
            {t('risk.add', 'Add risk')}
          </Button>
        )}
      </header>

      {error && <ErrorNotice message={error} />}

      {/* KPIs + heatmap */}
      {matrix && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatTile label={t('risk.openRisks', 'Open risks')} value={matrix.total} />
            <StatTile
              label={t('status.CRITICAL')}
              value={matrix.cells.filter((c) => riskScore(c.likelihood, c.impact) >= 15).reduce((s, c) => s + c.count, 0)}
              tone="critical"
            />
            <StatTile
              label={t('risk.treated', 'Treated')}
              value={matrix.byStatus.TREATED ?? 0}
            />
            <StatTile
              label={t('risk.accepted', 'Accepted')}
              value={matrix.byStatus.ACCEPTED ?? 0}
            />
          </div>

          <Card>
            <CardHeader
              title={t('risk.matrix', 'Risk matrix')}
              subtitle={t('risk.matrixHint', 'Count of open risks per likelihood × impact cell.')}
            />
            <RiskHeatmap cellMap={cellMap} />
          </Card>
        </>
      )}

      {/* Filters */}
      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs"
            placeholder={t('risk.searchPlaceholder', 'Search risks')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
          />
          <select className="oc-input sm:!w-auto" value={status} onChange={(e) => setStatus(e.target.value)}
                  aria-label={t('common.filter')}>
            <option value="">{t('risk.allStatuses', 'All statuses')}</option>
            {RISK_REGISTER_STATUSES.map((s) => <option key={s} value={s}>{t(`risk.status.${s}`, s)}</option>)}
          </select>
          <select className="oc-input sm:!w-auto" value={category} onChange={(e) => setCategory(e.target.value)}
                  aria-label={t('risk.categoryLabel', 'Category')}>
            <option value="">{t('risk.allCategories', 'All categories')}</option>
            {RISK_CATEGORIES.map((c) => <option key={c} value={c}>{t(`risk.category.${c}`, c)}</option>)}
          </select>
        </div>
      </Card>

      {/* Register table */}
      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : items.length === 0 ? (
        <Card><EmptyState icon={<IconShield className="w-10 h-10" />} title={t('risk.none', 'No risks recorded yet')} /></Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted border-b border-line">
                  <th className="px-3 py-2.5 font-medium">{t('risk.reference', 'Ref')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('risk.riskTitle', 'Risk')}</th>
                  <th className="px-3 py-2.5 font-medium hidden sm:table-cell">{t('risk.categoryLabel', 'Category')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('risk.inherent', 'Inherent')}</th>
                  <th className="px-3 py-2.5 font-medium hidden md:table-cell">{t('risk.treatmentLabel', 'Treatment')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('risk.residual', 'Residual')}</th>
                  <th className="px-3 py-2.5 font-medium hidden lg:table-cell">{t('risk.owner', 'Owner')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('risk.statusLabel', 'Status')}</th>
                  <th className="px-3 py-2.5 font-medium hidden lg:table-cell">{t('risk.reviewDate', 'Review')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => { setError(null); setEditing(r); }}
                    className="border-b border-line last:border-0 hover:bg-surface-sunken cursor-pointer"
                  >
                    <td className="px-3 py-2.5 font-mono text-xs text-ink-secondary whitespace-nowrap">{r.reference}</td>
                    <td className="px-3 py-2.5 text-ink min-w-[12rem]">{r.title}</td>
                    <td className="px-3 py-2.5 hidden sm:table-cell text-ink-secondary">
                      {t(`risk.category.${r.category}`, r.category)}
                    </td>
                    <td className="px-3 py-2.5"><ScoreChip score={r.inherentScore} /></td>
                    <td className="px-3 py-2.5 hidden md:table-cell text-ink-secondary">
                      {r.treatment ? t(`risk.treatment.${r.treatment}`, r.treatment) : '—'}
                    </td>
                    <td className="px-3 py-2.5">{r.residualScore != null ? <ScoreChip score={r.residualScore} /> : <span className="text-ink-muted">—</span>}</td>
                    <td className="px-3 py-2.5 hidden lg:table-cell text-ink-secondary truncate max-w-[10rem]">
                      {r.owner ? `${r.owner.firstName} ${r.owner.lastName}` : t('risk.unassigned', 'Unassigned')}
                    </td>
                    <td className="px-3 py-2.5">
                      <Pill tone={(TONE_FOR_STATUS[r.status] ?? 'neutral')}>{t(`risk.status.${r.status}`, r.status)}</Pill>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell text-ink-secondary whitespace-nowrap">{formatDate(r.reviewDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(creating || editing) && (
        <RiskForm
          risk={editing}
          canManage={canManage}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDelete={editing && canManage ? () => remove.mutate(editing.id) : undefined}
        />
      )}
    </div>
  );
}

/** A score value with a band-coloured chip. */
function ScoreChip({ score }: { score: number }) {
  return (
    <span
      className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 rounded-md text-xs font-semibold text-white tabular-nums"
      style={{ backgroundColor: `rgb(var(${BAND_VAR[riskBand(score)]}))` }}
    >
      {score}
    </span>
  );
}

// ---------------------------------------------------------------------------
//  Heatmap
// ---------------------------------------------------------------------------

function RiskHeatmap({ cellMap }: { cellMap: Map<string, number> }) {
  const { t } = useTranslation();
  // Impact descends down the rows (5 at top), likelihood ascends across (1 left).
  const impacts = [5, 4, 3, 2, 1];
  return (
    <div className="overflow-x-auto">
      <div className="inline-grid gap-1" style={{ gridTemplateColumns: 'auto repeat(5, minmax(3rem, 1fr))' }}>
        {/* top-left corner spacer */}
        <div />
        {AXES.map((l) => (
          <div key={`h-${l}`} className="text-center text-[11px] text-ink-muted pb-1">
            {t(axisKey(l), axisDefault(l))}
          </div>
        ))}
        {impacts.map((impact) => (
          <RowFragment key={`row-${impact}`} impact={impact} cellMap={cellMap} />
        ))}
        {/* axis captions */}
        <div />
        <div className="col-span-5 text-center text-[11px] font-medium text-ink-muted pt-1">
          {t('risk.likelihood', 'Likelihood')} →
        </div>
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        {t('risk.impact', 'Impact')} ↑ · {t('risk.matrixLegend', 'Colour is the inherent risk band; the number is how many open risks sit in that cell.')}
      </p>
    </div>
  );
}

function RowFragment({ impact, cellMap }: { impact: number; cellMap: Map<string, number> }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center justify-end pr-1.5 text-[11px] text-ink-muted whitespace-nowrap">
        {t(axisKey(impact), axisDefault(impact))}
      </div>
      {AXES.map((likelihood) => {
        const count = cellMap.get(`${likelihood}:${impact}`) ?? 0;
        const band = riskBand(riskScore(likelihood, impact));
        const alpha = count > 0 ? 0.9 : 0.16;
        return (
          <div
            key={`c-${likelihood}-${impact}`}
            className="aspect-square rounded-md flex items-center justify-center text-sm font-semibold tabular-nums"
            style={{
              backgroundColor: `rgb(var(${BAND_VAR[band]}) / ${alpha})`,
              color: count > 0 ? 'white' : 'rgb(var(--text-muted))',
            }}
            title={`${t('risk.likelihood', 'Likelihood')} ${likelihood} × ${t('risk.impact', 'Impact')} ${impact} = ${likelihood * impact}`}
          >
            {count > 0 ? count : ''}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
//  Create / edit form
// ---------------------------------------------------------------------------

interface OrgUser { id: string; firstName: string; lastName: string }
interface GapOption { id: string; title: string }
interface FrameworkOption { id: string; name: string }
interface ControlOption { organizationControlId: string | null; key: string; title: string }

function RiskForm({ risk, canManage, onClose, onDelete }: {
  risk: Risk | null;
  canManage: boolean;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState(() => ({
    title: risk?.title ?? '',
    description: risk?.description ?? '',
    category: risk?.category ?? 'OPERATIONAL',
    likelihood: risk?.likelihood ?? 3,
    impact: risk?.impact ?? 3,
    treatment: risk?.treatment ?? '',
    treatmentPlan: risk?.treatmentPlan ?? '',
    residualLikelihood: risk?.residualLikelihood ?? 0,
    residualImpact: risk?.residualImpact ?? 0,
    ownerId: risk?.owner?.id ?? '',
    status: risk?.status ?? 'IDENTIFIED',
    reviewDate: risk?.reviewDate ? risk.reviewDate.slice(0, 10) : '',
  }));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const inherent = riskScore(form.likelihood, form.impact);
  const hasResidual = form.residualLikelihood > 0 && form.residualImpact > 0;
  const residual = hasResidual ? riskScore(form.residualLikelihood, form.residualImpact) : null;

  // Owner list (best-effort; hidden when the user cannot read org users).
  const { data: users } = useQuery({
    queryKey: ['users', 'for-risk-owner'],
    queryFn: () => api.get<{ items: OrgUser[] }>(`/users${qs({ pageSize: 100 })}`),
    enabled: allows('user.read'),
  });

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      risk ? api.patch(`/risk-register/${risk.id}`, payload) : api.post('/risk-register', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risk-register'] });
      onClose();
    },
    onError: (err) => setError(describeError(err)),
  });

  const submit = () => {
    setError(null);
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      category: form.category,
      likelihood: form.likelihood,
      impact: form.impact,
      treatment: form.treatment || null,
      treatmentPlan: form.treatmentPlan.trim() || null,
      residualLikelihood: form.residualLikelihood > 0 ? form.residualLikelihood : null,
      residualImpact: form.residualImpact > 0 ? form.residualImpact : null,
      ownerId: form.ownerId || null,
      status: form.status,
      reviewDate: form.reviewDate ? new Date(form.reviewDate).toISOString() : null,
    };
    save.mutate(payload);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={risk ? `${risk.reference} · ${t('risk.edit', 'Edit risk')}` : t('risk.add', 'Add risk')}
      footer={
        canManage ? (
          <>
            {onDelete && (
              <Button variant="danger" onClick={onDelete} className="sm:mr-auto">
                {t('common.delete', 'Delete')}
              </Button>
            )}
            <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
            <Button variant="primary" onClick={submit} disabled={save.isPending || !form.title.trim()}>
              {t('common.save', 'Save')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
        )
      }
    >
      <div className="space-y-1">
        {error && <ErrorNotice message={error} />}

        <fieldset disabled={!canManage} className="space-y-1">
          <Field label={t('risk.riskTitle', 'Risk')} required>
            <input className="oc-input" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={500} />
          </Field>

          <Field label={t('risk.description', 'Description')}>
            <textarea className="oc-input min-h-[4rem]" value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={10_000} />
          </Field>

          <Field label={t('risk.categoryLabel', 'Category')} required>
            <select className="oc-input" value={form.category} onChange={(e) => set('category', e.target.value)}>
              {RISK_CATEGORIES.map((c) => <option key={c} value={c}>{t(`risk.category.${c}`, c)}</option>)}
            </select>
          </Field>

          {/* Inherent assessment */}
          <p className="oc-label mt-3">{t('risk.inherentAssessment', 'Inherent assessment')}</p>
          <div className="grid grid-cols-2 gap-3">
            <AxisSelect label={t('risk.likelihood', 'Likelihood')} value={form.likelihood} onChange={(v) => set('likelihood', v)} />
            <AxisSelect label={t('risk.impact', 'Impact')} value={form.impact} onChange={(v) => set('impact', v)} />
          </div>
          <div className="flex items-center gap-2 mb-3 text-sm">
            <span className="text-ink-muted">{t('risk.inherentScore', 'Inherent score')}:</span>
            <ScoreChip score={inherent} />
            <Pill tone={scoreTone(inherent)}>{t(`status.${riskBand(inherent)}`)}</Pill>
          </div>

          {/* Treatment */}
          <Field label={t('risk.treatmentLabel', 'Treatment')}>
            <select className="oc-input" value={form.treatment} onChange={(e) => set('treatment', e.target.value)}>
              <option value="">{t('risk.noTreatment', 'Not decided')}</option>
              {RISK_TREATMENTS.map((tr) => <option key={tr} value={tr}>{t(`risk.treatment.${tr}`, tr)}</option>)}
            </select>
          </Field>
          <Field label={t('risk.treatmentPlan', 'Treatment plan')}>
            <textarea className="oc-input min-h-[3.5rem]" value={form.treatmentPlan} onChange={(e) => set('treatmentPlan', e.target.value)} maxLength={10_000} />
          </Field>

          {/* Residual assessment */}
          <p className="oc-label mt-3">{t('risk.residualAssessment', 'Residual assessment (after treatment)')}</p>
          <div className="grid grid-cols-2 gap-3">
            <AxisSelect label={t('risk.likelihood', 'Likelihood')} value={form.residualLikelihood} onChange={(v) => set('residualLikelihood', v)} allowNone />
            <AxisSelect label={t('risk.impact', 'Impact')} value={form.residualImpact} onChange={(v) => set('residualImpact', v)} allowNone />
          </div>
          {residual != null && (
            <div className="flex items-center gap-2 mb-3 text-sm">
              <span className="text-ink-muted">{t('risk.residualScore', 'Residual score')}:</span>
              <ScoreChip score={residual} />
              <Pill tone={scoreTone(residual)}>{t(`status.${riskBand(residual)}`)}</Pill>
            </div>
          )}

          {/* Owner / status / review */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('risk.owner', 'Owner')}>
              <select className="oc-input" value={form.ownerId} onChange={(e) => set('ownerId', e.target.value)}>
                <option value="">{t('risk.unassigned', 'Unassigned')}</option>
                {(users?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
            </Field>
            <Field label={t('risk.statusLabel', 'Status')}>
              <select className="oc-input" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {RISK_REGISTER_STATUSES.map((s) => <option key={s} value={s}>{t(`risk.status.${s}`, s)}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t('risk.reviewDate', 'Review date')}>
            <input type="date" className="oc-input" value={form.reviewDate} onChange={(e) => set('reviewDate', e.target.value)} />
          </Field>
        </fieldset>

        {/* Links — only meaningful once the risk exists */}
        {risk && <RiskLinks risk={risk} canManage={canManage} />}
      </div>
    </Modal>
  );
}

function AxisSelect({ label, value, onChange, allowNone }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  allowNone?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <select className="oc-input" value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {allowNone && <option value={0}>—</option>}
        {AXES.map((n) => (
          <option key={n} value={n}>{n} · {t(axisKey(n), axisDefault(n))}</option>
        ))}
      </select>
    </Field>
  );
}

// ---------------------------------------------------------------------------
//  Links to gaps / controls / frameworks
// ---------------------------------------------------------------------------

function RiskLinks({ risk, canManage }: { risk: Risk; canManage: boolean }) {
  const { t } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<'GAP' | 'CONTROL' | 'FRAMEWORK'>('GAP');
  const [entityId, setEntityId] = useState('');

  const { data: detail } = useQuery({
    queryKey: ['risk-register', risk.id],
    queryFn: () => api.get<Risk>(`/risk-register/${risk.id}`),
    initialData: risk,
  });

  const { data: gaps } = useQuery({
    queryKey: ['gaps', 'for-risk-link'],
    queryFn: () => api.get<{ items: GapOption[] }>(`/gaps${qs({ pageSize: 500, status: 'OPEN' })}`),
    enabled: canManage && allows('gap.read') && type === 'GAP',
  });
  const { data: frameworks } = useQuery({
    queryKey: ['frameworks', 'for-risk-link'],
    queryFn: () => api.get<FrameworkOption[]>('/frameworks'),
    enabled: canManage && allows('framework.read') && type === 'FRAMEWORK',
  });
  const { data: catalogue } = useQuery({
    queryKey: ['catalogue', 'for-risk-link'],
    queryFn: () => api.get<{ items: ControlOption[] }>('/catalogue'),
    enabled: canManage && allows('framework.read') && type === 'CONTROL',
  });

  const controlOptions = (catalogue?.items ?? []).filter((c) => c.organizationControlId);

  const addLink = useMutation({
    mutationFn: () => api.post(`/risk-register/${risk.id}/links`, { entityType: type, entityId }),
    onSuccess: () => {
      setEntityId('');
      void queryClient.invalidateQueries({ queryKey: ['risk-register', risk.id] });
      void queryClient.invalidateQueries({ queryKey: ['risk-register'] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const removeLink = useMutation({
    mutationFn: (linkId: string) => api.delete(`/risk-register/${risk.id}/links/${linkId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risk-register', risk.id] });
      void queryClient.invalidateQueries({ queryKey: ['risk-register'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const links = detail?.links ?? [];

  return (
    <div className="pt-4 mt-2 border-t border-line">
      <p className="oc-label">{t('risk.links', 'Linked gaps, controls & frameworks')}</p>
      {error && <ErrorNotice message={error} />}

      {links.length === 0 ? (
        <p className="text-sm text-ink-muted mb-2">{t('risk.noLinks', 'Nothing linked yet.')}</p>
      ) : (
        <ul className="space-y-1.5 mb-3">
          {links.map((link) => (
            <li key={link.id} className="flex items-center gap-2 rounded-lg bg-surface-sunken px-3 py-2">
              <span className="oc-pill-neutral shrink-0">{t(`risk.linkType.${link.entityType}`, link.entityType)}</span>
              <span className="text-sm text-ink truncate flex-1">{link.label ?? link.entityId}</span>
              {canManage && (
                <button
                  onClick={() => removeLink.mutate(link.id)}
                  className="text-xs text-critical hover:underline shrink-0"
                >
                  {t('common.remove', 'Remove')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            className="oc-input sm:!w-auto"
            value={type}
            onChange={(e) => { setType(e.target.value as typeof type); setEntityId(''); }}
            aria-label={t('risk.linkTypeLabel', 'Link type')}
          >
            <option value="GAP">{t('risk.linkType.GAP', 'Gap')}</option>
            <option value="CONTROL">{t('risk.linkType.CONTROL', 'Control')}</option>
            <option value="FRAMEWORK">{t('risk.linkType.FRAMEWORK', 'Framework')}</option>
          </select>
          <select className="oc-input flex-1" value={entityId} onChange={(e) => setEntityId(e.target.value)} aria-label={t('risk.linkTarget', 'Target')}>
            <option value="">{t('risk.selectTarget', 'Select…')}</option>
            {type === 'GAP' && (gaps?.items ?? []).map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            {type === 'FRAMEWORK' && (frameworks ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            {type === 'CONTROL' && controlOptions.map((c) => (
              <option key={c.organizationControlId!} value={c.organizationControlId!}>{c.key} — {c.title}</option>
            ))}
          </select>
          <Button onClick={() => addLink.mutate()} disabled={!entityId || addLink.isPending}>
            {t('risk.addLink', 'Link')}
          </Button>
        </div>
      )}
    </div>
  );
}
